import type { ExportJob } from './videoExport';

/**
 * The editor hands a video export to a page of its own (`/export/:documentId`, `ExportRoute`).
 *
 * The page is opened in the click itself, before anything is ready, or the browser takes it for a
 * pop-up and blocks it; the job follows by message once the editor has gathered it. The page
 * asks for it (it may load before or after the job is ready), and the editor answers only a page
 * of its own origin that names the job it opened.
 */

const READY = 'artwork-export-ready';
const JOB = 'artwork-export-job';

type Answer = { type: typeof JOB; job: string; payload?: ExportJob; error?: string };

/** Opens the export page for a new job, or null when the browser refused to open it. */
export function openExportPage(documentId: string): { win: Window; job: string } | null {
  const job = crypto.randomUUID();
  const win = window.open(`/export/${encodeURIComponent(documentId)}?job=${job}`, '_blank');
  return win ? { win, job } : null;
}

/** Answers the page's ask with the job, or with why it could not be made. Stops listening after ten minutes. */
export function handOver(opened: { win: Window; job: string }, payload: Promise<ExportJob>): void {
  let answer: Answer | null = null;
  payload.then(
    (made) => { answer = { type: JOB, job: opened.job, payload: made }; },
    (error: unknown) => { answer = { type: JOB, job: opened.job, error: error instanceof Error ? error.message : 'The export could not be prepared.' }; },
  );
  const listen = async (event: MessageEvent) => {
    if (event.origin !== window.location.origin || event.source !== opened.win) return;
    const data = event.data as { type?: string; job?: string } | null;
    if (data?.type !== READY || data.job !== opened.job) return;
    await payload.catch(() => undefined);
    if (answer) opened.win.postMessage(answer, window.location.origin);
    window.removeEventListener('message', listen);
    clearTimeout(timer);
  };
  window.addEventListener('message', listen);
  const timer = setTimeout(() => window.removeEventListener('message', listen), 10 * 60_000);
}

/**
 * Each job the export page was handed, kept in this browser for a day, so the page reloaded still
 * has its file: the editor hands a job over once (Tarek, 3 Nov: "in refresh it's not getting the
 * file from the editor").
 */
const KEPT = { db: 'artwork-export-jobs', store: 'jobs', days: 1 };

function keptJobs(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(KEPT.db, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(KEPT.store);
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('No storage.'));
  });
}

async function keptJob(job: string): Promise<ExportJob | null> {
  try {
    const db = await keptJobs();
    try {
      return await new Promise<ExportJob | null>((resolve) => {
        const read = db.transaction(KEPT.store).objectStore(KEPT.store).get(job);
        read.onsuccess = () => resolve((read.result as { payload: ExportJob } | undefined)?.payload ?? null);
        read.onerror = () => resolve(null);
      });
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

async function keepJob(job: string, payload: ExportJob): Promise<void> {
  try {
    const db = await keptJobs();
    const store = db.transaction(KEPT.store, 'readwrite').objectStore(KEPT.store);
    store.put({ payload, at: Date.now() }, job);
    // Yesterday's jobs go as today's come in.
    const old = Date.now() - KEPT.days * 86_400_000;
    const walk = store.openCursor();
    walk.onsuccess = () => {
      const cursor = walk.result;
      if (!cursor) return;
      if (((cursor.value as { at?: number }).at ?? 0) < old) cursor.delete();
      cursor.continue();
    };
    store.transaction.oncomplete = () => db.close();
  } catch {
    /* no storage here (a private window): a reload asks the editor again */
  }
}

/** The export page's side: the job kept from before a reload, else asks the editor that opened it, until it answers or gives up. */
export async function receiveExportJob(job: string, timeoutMs = 60_000): Promise<ExportJob> {
  const kept = await keptJob(job);
  if (kept) return kept;
  const payload = await askOpener(job, timeoutMs);
  void keepJob(job, payload);
  return payload;
}

function askOpener(job: string, timeoutMs: number): Promise<ExportJob> {
  return new Promise((resolve, reject) => {
    const opener = window.opener as Window | null;
    if (!opener) return reject(new Error('This export page has lost the file it was opened from. Start the export again from the editor.'));
    const started = Date.now();
    const ask = () => {
      if (Date.now() - started > timeoutMs) return done(new Error('The editor did not hand the export over. Start it again from the editor.'));
      try {
        opener.postMessage({ type: READY, job }, window.location.origin);
      } catch {
        return done(new Error('This export page has lost the file it was opened from. Start the export again from the editor.'));
      }
    };
    const listen = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== opener) return;
      const data = event.data as Answer | null;
      if (data?.type !== JOB || data.job !== job) return;
      if (data.payload) done(undefined, data.payload);
      else done(new Error(data.error ?? 'The export could not be prepared.'));
    };
    const timer = setInterval(ask, 400);
    const done = (error?: Error, payload?: ExportJob) => {
      clearInterval(timer);
      window.removeEventListener('message', listen);
      if (error) reject(error);
      else resolve(payload!);
    };
    window.addEventListener('message', listen);
    ask();
  });
}
